<!-- Coupons Table Component -->
<!-- Mobile responsive with row expansion matching customers-table pattern -->
<div class="coupons-table">
  <p-table
    [value]="tableData"
    [lazy]="true"
    [paginator]="true"
    [rows]="data?.PageSize ?? defaultPageSize"
    [totalRecords]="data?.TotalCount ?? 0"
    [first]="firstRecord"
    [rowsPerPageOptions]="pageSizeOptions"
    [loading]="loading"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} coupons"
    (onLazyLoad)="onLazyLoad($event)"
    [scrollHeight]="scrollHeight"
    [scrollable]="true"
    scrollDirection="both"
    size="small"
    styleClass="p-datatable-sm p-datatable-coupons"
    responsiveLayout="stack"
    breakpoint="768px"
    dataKey="Id"
    [rowExpandMode]="'single'"
    [expandedRowKeys]="expandedRows"
    (onRowExpand)="onRowExpand($event)"
    (onRowCollapse)="onRowCollapse($event)"
  >
    <!-- Table Header -->
    <ng-template pTemplate="header">
      <tr>
        <!-- Expand/Collapse Column (Frozen Left) -->
        <th
          style="width: 2.5rem"
          class="text-center"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
        ></th>
        <th
          pSortableColumn="Id"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
          style="width: 70px"
        >
          ID
          <p-sortIcon field="Id" />
        </th>
        <th pSortableColumn="Code" style="min-width: 120px">
          Code
          <p-sortIcon field="Code" />
        </th>
        <th pSortableColumn="Name" style="min-width: 150px">
          Name
          <p-sortIcon field="Name" />
        </th>
        <th pSortableColumn="DiscountType" style="min-width: 140px">
          Type
          <p-sortIcon field="DiscountType" />
        </th>
        <th pSortableColumn="Amount" style="width: 100px">
          Amount
          <p-sortIcon field="Amount" />
        </th>
        <th style="width: 90px" class="desktop-only-header">Recurring</th>
        <th style="width: 90px" class="desktop-only-header">Max/cust</th>
        <th style="width: 90px" class="desktop-only-header">Max/site</th>
        <th style="width: 80px" class="desktop-only-header">Min units</th>
        <th style="width: 90px" class="desktop-only-header">Min subtotal</th>
        <th style="width: 80px" class="desktop-only-header">Min cycles</th>
        <th style="width: 80px" class="desktop-only-header">Max cycles</th>
        <th pSortableColumn="ExpirationDate" style="width: 120px">
          Expires
          <p-sortIcon field="ExpirationDate" />
        </th>
        <th
          pFrozenColumn
          [frozen]="true"
          alignFrozen="right"
          style="width: 3rem; text-align: center"
        >
          Actions
        </th>
      </tr>
    </ng-template>

    <!-- Table Body -->
    <ng-template pTemplate="body" let-coupon let-expanded="expanded">
      <tr
        class="coupon-row"
        [class.clickable-row]="true"
        [class.inactive-row]="!coupon.Active"
        (click)="toggleRowExpansion(coupon)"
      >
        <!-- Row Expansion Toggle (Frozen Left) -->
        <td
          class="text-center expand-column"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
          (click)="$event.stopPropagation()"
        >
          <p-button
            [pRowToggler]="coupon"
            type="button"
            pRipple
            [text]="true"
            [rounded]="true"
            [plain]="true"
            size="small"
            class="text-gray-400 hover:text-gray-600"
            [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
            [disabled]="loading"
            pTooltip="Click to expand/collapse details"
            tooltipPosition="top"
          />
        </td>

        <!-- ID Column (Frozen Left) -->
        <td class="id-column" pFrozenColumn [frozen]="true" alignFrozen="left">
          <a
            href="javascript:;"
            class="id-link"
            (click)="onIdClick(coupon); $event.stopPropagation()"
            pTooltip="Edit coupon"
            tooltipPosition="top"
          >
            {{ coupon.Id }}
          </a>
        </td>

        <!-- Code Column -->
        <td class="code-column">
          <span class="p-column-title">Code</span>
          <span class="coupon-code">{{ coupon.Code }}</span>
          @if (!coupon.Active) {
            <p-tag value="Inactive" severity="secondary" class="ml-2" />
          }
        </td>

        <!-- Name Column -->
        <td class="name-column">
          <span class="p-column-title">Name</span>
          <span class="coupon-name">{{ coupon.Name || "-" }}</span>
        </td>

        <!-- Discount Type Column -->
        <td class="type-column">
          <span class="p-column-title">Type</span>
          <p-tag
            [value]="getDiscountLabel(coupon.DiscountType)"
            [severity]="getDiscountTypeSeverity(coupon.DiscountType)"
          />
        </td>

        <!-- Amount Column -->
        <td class="amount-column">
          <span class="p-column-title">Amount</span>
          <span class="coupon-amount">{{
            formatAmount(coupon.DiscountType, coupon.Amount)
          }}</span>
        </td>

        <!-- Recurring Column (desktop only) -->
        <td class="desktop-only-cell">
          <span class="p-column-title">Recurring</span>
          @if (coupon.MaxCyclesPerScheduledOrder === 1) {
            <span class="text-gray-500">One time</span>
          } @else if (coupon.MaxCyclesPerScheduledOrder) {
            <span>{{ coupon.MaxCyclesPerScheduledOrder }} cycles</span>
          } @else {
            <span class="text-green-600">Unlimited</span>
          }
        </td>

        <!-- Max/Customer Column (desktop only) -->
        <td class="desktop-only-cell">
          <span class="p-column-title">Max/Cust</span>
          @if (coupon.MaxAssignmentsPerCustomer) {
            <span>{{ coupon.MaxAssignmentsPerCustomer }}</span>
          } @else {
            <span class="text-gray-400">Unlimited</span>
          }
        </td>

        <!-- Max/Site Column (desktop only) -->
        <td class="desktop-only-cell">
          <span class="p-column-title">Max/Site</span>
          @if (coupon.MaxAssignmentsPerSite) {
            <span>{{ coupon.MaxAssignmentsPerSite }}</span>
          } @else {
            <span class="text-gray-400">Unlimited</span>
          }
        </td>

        <!-- Min Units Column (desktop only) -->
        <td class="desktop-only-cell">
          <span class="p-column-title">Min Units</span>
          @if (coupon.MinUnits) {
            <span>{{ coupon.MinUnits }}</span>
          } @else {
            <span class="text-gray-400">-</span>
          }
        </td>

        <!-- Min Subtotal Column (desktop only) -->
        <td class="desktop-only-cell">
          <span class="p-column-title">Min Subtotal</span>
          @if (coupon.MinSubtotal) {
            <span>{{ coupon.MinSubtotal | currency }}</span>
          } @else {
            <span class="text-gray-400">-</span>
          }
        </td>

        <!-- Min Cycles Column (desktop only) -->
        <td class="desktop-only-cell">
          <span class="p-column-title">Min Cycles</span>
          @if (coupon.MinScheduledOrderCycles) {
            <span>{{ coupon.MinScheduledOrderCycles }}</span>
          } @else {
            <span class="text-gray-400">-</span>
          }
        </td>

        <!-- Max Cycles Column (desktop only) -->
        <td class="desktop-only-cell">
          <span class="p-column-title">Max Cycles</span>
          @if (coupon.MaxScheduledOrderCycles) {
            <span>{{ coupon.MaxScheduledOrderCycles }}</span>
          } @else {
            <span class="text-gray-400">-</span>
          }
        </td>

        <!-- Expiration Date Column -->
        <td class="expires-column">
          <span class="p-column-title">Expires</span>
          @if (coupon.ExpirationDate) {
            <span [class.text-red-500]="isExpired(coupon.ExpirationDate)">
              {{ coupon.ExpirationDate | date: "MMM d, yyyy" }}
            </span>
          } @else {
            <span class="text-gray-400">No expiration</span>
          }
        </td>

        <!-- Actions Column (Frozen Right) -->
        <td
          class="actions-column text-center"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="right"
          (click)="$event.stopPropagation()"
        >
          <button
            pButton
            type="button"
            icon="pi pi-ellipsis-v"
            class="p-button-rounded p-button-text p-button-sm"
            (click)="showActionsMenu($event, coupon)"
            pTooltip="Actions"
            tooltipPosition="left"
          ></button>
        </td>
      </tr>
    </ng-template>

    <!-- Row Expansion Template -->
    <ng-template pTemplate="expandedrow" let-coupon>
      <tr class="expanded-row">
        <td colspan="15" class="expanded-row-cell">
          <div class="expanded-row-content">
            <!-- Quick Actions -->
            <div class="quick-actions-section">
              <p-button
                label="Edit"
                icon="pi pi-pencil"
                size="small"
                severity="secondary"
                [outlined]="true"
                (click)="rowAction.emit({ action: 'edit', coupon })"
              />
              <p-button
                label="Delete"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                [outlined]="true"
                (click)="rowAction.emit({ action: 'delete', coupon })"
              />
            </div>

            <!-- Mobile-only: Fields hidden from card (shown on desktop table) -->
            <div class="detail-section mobile-only-section">
              <div class="section-header">Limits & Requirements</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Recurring</dt>
                  <dd>
                    @if (coupon.MaxCyclesPerScheduledOrder === 1) {
                      One time
                    } @else if (coupon.MaxCyclesPerScheduledOrder) {
                      {{ coupon.MaxCyclesPerScheduledOrder }} cycles
                    } @else {
                      <span class="text-green-600">Unlimited</span>
                    }
                  </dd>
                </div>
                <div class="detail-item">
                  <dt>Max/Customer</dt>
                  <dd>{{ coupon.MaxAssignmentsPerCustomer || "Unlimited" }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Max/Site</dt>
                  <dd>{{ coupon.MaxAssignmentsPerSite || "Unlimited" }}</dd>
                </div>
                @if (coupon.MinUnits) {
                  <div class="detail-item">
                    <dt>Min Units</dt>
                    <dd>{{ coupon.MinUnits }}</dd>
                  </div>
                }
                @if (coupon.MinSubtotal) {
                  <div class="detail-item">
                    <dt>Min Subtotal</dt>
                    <dd>{{ coupon.MinSubtotal | currency }}</dd>
                  </div>
                }
                @if (coupon.MinScheduledOrderCycles) {
                  <div class="detail-item">
                    <dt>Min Cycles</dt>
                    <dd>{{ coupon.MinScheduledOrderCycles }}</dd>
                  </div>
                }
                @if (coupon.MaxScheduledOrderCycles) {
                  <div class="detail-item">
                    <dt>Max Cycles</dt>
                    <dd>{{ coupon.MaxScheduledOrderCycles }}</dd>
                  </div>
                }
              </dl>
            </div>

            <!-- Coupon Details -->
            <div class="detail-section">
              <div class="section-header">Coupon Details</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Status</dt>
                  <dd>
                    @if (coupon.Active) {
                      <p-tag value="Active" severity="success" />
                    } @else {
                      <p-tag value="Inactive" severity="secondary" />
                    }
                  </dd>
                </div>
                <div class="detail-item">
                  <dt>Stackable</dt>
                  <dd>{{ coupon.IsStackable ? "Yes" : "No" }}</dd>
                </div>
                @if (coupon.ProductId) {
                  <div class="detail-item">
                    <dt>Product</dt>
                    <dd>
                      {{ coupon.Product?.Name || "ID: " + coupon.ProductId }}
                    </dd>
                  </div>
                }
                @if (coupon.ProductGroupId) {
                  <div class="detail-item">
                    <dt>Product Group</dt>
                    <dd>
                      {{
                        coupon.ProductGroup?.Name ||
                          "ID: " + coupon.ProductGroupId
                      }}
                    </dd>
                  </div>
                }
              </dl>
            </div>

            <!-- Usage Limits -->
            <div class="detail-section">
              <div class="section-header">Usage Limits</div>
              <dl class="detail-list">
                @if (coupon.MaxCyclesPerScheduledOrder) {
                  <div class="detail-item">
                    <dt>Per Order</dt>
                    <dd>{{ coupon.MaxCyclesPerScheduledOrder }}</dd>
                  </div>
                }
                @if (coupon.MaxCyclesPerCustomer) {
                  <div class="detail-item">
                    <dt>Per Customer</dt>
                    <dd>{{ coupon.MaxCyclesPerCustomer }}</dd>
                  </div>
                }
                @if (coupon.MaxAssignmentsPerSite) {
                  <div class="detail-item">
                    <dt>Max Assignments</dt>
                    <dd>{{ coupon.MaxAssignmentsPerSite }}</dd>
                  </div>
                }
                @if (coupon.MaxDiscountPerCustomer) {
                  <div class="detail-item">
                    <dt>Max Discount</dt>
                    <dd>{{ coupon.MaxDiscountPerCustomer | currency }}</dd>
                  </div>
                }
                @if (
                  !coupon.MaxCyclesPerScheduledOrder &&
                  !coupon.MaxCyclesPerCustomer &&
                  !coupon.MaxAssignmentsPerSite &&
                  !coupon.MaxDiscountPerCustomer
                ) {
                  <div class="detail-item">
                    <dt>Limits</dt>
                    <dd>No limits set</dd>
                  </div>
                }
              </dl>
            </div>

            <!-- Order Requirements (show only if any are set) -->
            @if (
              coupon.MinSubtotal ||
              coupon.MaxSubtotal ||
              coupon.MinUnits ||
              coupon.MaxUnits
            ) {
              <div class="detail-section">
                <div class="section-header">Order Requirements</div>
                <dl class="detail-list">
                  @if (coupon.MinSubtotal) {
                    <div class="detail-item">
                      <dt>Min Subtotal</dt>
                      <dd>{{ coupon.MinSubtotal | currency }}</dd>
                    </div>
                  }
                  @if (coupon.MaxSubtotal) {
                    <div class="detail-item">
                      <dt>Max Subtotal</dt>
                      <dd>{{ coupon.MaxSubtotal | currency }}</dd>
                    </div>
                  }
                  @if (coupon.MinUnits) {
                    <div class="detail-item">
                      <dt>Min Units</dt>
                      <dd>{{ coupon.MinUnits }}</dd>
                    </div>
                  }
                  @if (coupon.MaxUnits) {
                    <div class="detail-item">
                      <dt>Max Units</dt>
                      <dd>{{ coupon.MaxUnits }}</dd>
                    </div>
                  }
                </dl>
              </div>
            }

            <!-- Timeline -->
            <div class="detail-section">
              <div class="section-header">Timeline</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Created</dt>
                  <dd>{{ formatDate(coupon.CreatedUtc) }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Updated</dt>
                  <dd>{{ formatDate(coupon.UpdatedUtc) }}</dd>
                </div>
                @if (coupon.ExpirationDate) {
                  <div class="detail-item">
                    <dt>Expires</dt>
                    <dd [class.text-red-500]="isExpired(coupon.ExpirationDate)">
                      {{ coupon.ExpirationDate | date: "MMM d, yyyy" }}
                    </dd>
                  </div>
                }
              </dl>
            </div>
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Empty State -->
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="15" class="text-center py-8">
          <div class="empty-state">
            <i class="pi pi-ticket text-4xl text-gray-300 mb-3"></i>
            <p class="text-gray-500 mb-0">No coupons found</p>
            <p class="text-gray-400 text-sm">
              Try adjusting your filters or create a new coupon
            </p>
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Loading State -->
    <ng-template pTemplate="loadingbody">
      <app-table-skeleton-rows [rows]="5" [columns]="skeletonColumns" />
    </ng-template>
  </p-table>

  <!-- Context Menu -->
  <p-menu #actionsMenu [popup]="true" [model]="menuItems" appendTo="body" />
</div>
